/**
 * First-launch welcome note: teaches the app inside the app.
 */
import { getData } from "./store";
import { openItem } from "@/commands/nav";
import { useTabs } from "@/store/tabs";

const FLAG_PREFIX = "hn.onboarded.";

const WELCOME = `Welcome! This note is yours to edit — everything here is plain markdown.

## The basics

- Press **Ctrl+P** to find or create any note. Empty search shows recents; **Shift+Enter** creates a note with exactly what you typed.
- Press **Ctrl+Shift+P** (or type \`>\` in the switcher) for every command.
- **Ctrl+N** makes a note, **Ctrl+D** opens today's daily note.
- Formatting renders live; put your cursor on a line to see the raw markdown.

## Try these

- [ ] Make this checkbox done by clicking it
- [ ] Link to another note by typing \`[[\` — autocomplete appears
- [ ] Add a #tag anywhere, then search \`#tag\` in the switcher
- [ ] Drag this note onto a folder in the sidebar

## Your notes are safe

The status chip in the bottom-left always tells the truth: **Local only** means this browser holds the only copy. Sign in (it's free) and notes sync live to every device, with automatic version history and a trash that keeps deleted notes for 30 days.

> Templates live in a root folder named "Templates" — a note called "Daily" there becomes your daily note template. Placeholders: \`{{date}}\`, \`{{time}}\`, \`{{title}}\`.

Happy writing!
`;

/** Seed the welcome note into the CURRENT workspace, once per workspace. */
export async function seedOnboarding(): Promise<void> {
  const data = getData();
  const flag = FLAG_PREFIX + data.workspaceId;
  if (localStorage.getItem(flag) || localStorage.getItem("hn.onboarded")) return;
  await data.workspace.whenLoaded;
  if (data.workspace.getSnapshot().length > 0) return; // not a fresh workspace
  localStorage.setItem(flag, new Date().toISOString());
  const id = data.workspace.createItem("note", null, "Welcome to Hydra Notes");
  const handle = data.notes.open(id);
  await handle.whenLoaded;
  handle.doc.transact(() => {
    handle.text.insert(0, WELCOME);
  });
  handle.release();
  // never steal focus from someone already writing: only surface the welcome
  // note when nothing else is open yet (seeding is async and can land after
  // the user's first Ctrl+N on a slow boot)
  const groups = useTabs.getState().groups;
  const anythingOpen = groups.some((g) => g.tabs.length > 0);
  if (!anythingOpen) openItem(id);
}
